import toast from 'react-hot-toast';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Building2, Check, ChevronDown, ChevronRight, FlaskConical } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import WorkspaceTypeBadge from './WorkspaceTypeBadge';

interface WorkspaceSwitcherProps {
  collapsed?: boolean;
}

function formatRoleLabel(role: string) {
  if (role === 'qa_event_viewer') return 'QA Event Viewer';
  return role.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getWorkspaceRoleLabel(workspace: { role: string; roleName?: string }) {
  return workspace.roleName || formatRoleLabel(workspace.role);
}

function WorkspaceTypeIcon({ workspaceType, className }: { workspaceType?: 'live' | 'sandbox'; className: string }) {
  const Icon = workspaceType === 'sandbox' ? FlaskConical : Activity;
  const colorClass = workspaceType === 'sandbox'
    ? 'text-violet-600 dark:text-violet-400'
    : 'text-slate-400 dark:text-zinc-500';
  return <Icon aria-hidden="true" className={`${className} ${colorClass}`} />;
}

export function WorkspaceSwitcher({ collapsed = false }: WorkspaceSwitcherProps) {
  const { user, switchWorkspace } = useAuth();
  // The signed-in user's memberships are loaded from the member-safe /teams
  // endpoint. Do not call the workspace-management endpoint here: viewers and
  // QA users still need to switch to another workspace they belong to.
  const workspaces = user?.workspaces || [];
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ bottom: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  const currentWorkspaceId = apiService.getWorkspaceId();
  const currentWorkspace = workspaces.find(ws => ws.workspaceId === currentWorkspaceId) || workspaces[0];
  const availableWorkspaces = workspaces.filter(ws => ws.workspaceId !== currentWorkspace?.workspaceId);
  const dropdownWorkspaces = collapsed
    ? [...availableWorkspaces, ...(currentWorkspace ? [currentWorkspace] : [])]
    : availableWorkspaces;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const insideButton = buttonRef.current?.contains(target) ?? false;
      const insideDropdown = portalDropdownRef.current?.contains(target) ?? false;
      if (!insideButton && !insideDropdown) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        // Join the workspace options and selected-workspace row into one panel
        // on the expanded sidebar. Keep a small separation in collapsed mode.
        bottom: window.innerHeight - rect.top + (collapsed ? 8 : 0),
        left: rect.left,
        width: collapsed ? 224 : rect.width,
      });
    } else {
      setDropdownPos(null);
    }
  }, [isOpen, collapsed]);

  async function handleSwitch(workspaceId: string) {
    if (workspaceId === currentWorkspaceId) {
      setIsOpen(false);
      return;
    }
    setIsOpen(false);
    try {
      await switchWorkspace(workspaceId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to switch workspace. Please try again.');
    }
  }

  if (workspaces.length === 0) {
    return null;
  }

  const dropdownMenu = (
    <div
      ref={portalDropdownRef}
      className={`fixed bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md border border-slate-200/80 dark:border-zinc-800/80 z-100 overflow-hidden ${collapsed
        ? 'rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.5)] py-1.5'
        : 'rounded-t-xl rounded-b-none border-b-0 shadow-[0_-10px_28px_rgba(0,0,0,0.08)] dark:shadow-[0_-10px_28px_rgba(0,0,0,0.45)] py-0'}`}
      style={dropdownPos ? { bottom: dropdownPos.bottom, left: dropdownPos.left, width: dropdownPos.width } : undefined}
    >
      <div className={`max-h-52 overflow-y-auto custom-scrollbar ${collapsed ? 'px-1.5' : 'px-0'}`}>
        {dropdownWorkspaces.map(ws => (
          <button
            key={ws.workspaceId}
            onClick={() => handleSwitch(ws.workspaceId)}
            className={`flex items-center gap-2 w-full px-3 py-2 text-left text-xs transition-colors ${collapsed && ws.workspaceId === currentWorkspace?.workspaceId
              ? ws.workspaceType === 'sandbox'
                ? 'bg-violet-100/90 hover:bg-violet-100 dark:bg-violet-950/50 dark:hover:bg-violet-950/60'
                : 'bg-emerald-100/90 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:hover:bg-emerald-950/60'
              : 'hover:bg-slate-50 dark:hover:bg-white/5'}`}
          >
            <Building2 aria-hidden="true" className="w-4 h-4 text-slate-400 dark:text-zinc-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="truncate font-medium text-slate-900 dark:text-zinc-100">
                  {ws.name}
                </p>
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <WorkspaceTypeBadge workspaceType={ws.workspaceType} />
                {ws.role && <span className="truncate text-[10px] text-slate-500 dark:text-zinc-400 capitalize">· {getWorkspaceRoleLabel(ws)}</span>}
              </div>
            </div>
            {collapsed && ws.workspaceId === currentWorkspace?.workspaceId && (
              <Check className={`w-3.5 h-3.5 shrink-0 ${ws.workspaceType === 'sandbox' ? 'text-violet-600 dark:text-violet-400' : 'text-emerald-600 dark:text-emerald-400'}`} aria-label="Selected workspace" />
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // Collapsed mode — logo or first-letter avatar with building background
  if (collapsed) {
    const workspaceInitial = (currentWorkspace?.name || 'Workspace').charAt(0).toUpperCase();
    return (
      <div className="relative flex justify-center">
        <button
          ref={buttonRef}
          onClick={() => (collapsed ? workspaces.length > 0 : availableWorkspaces.length > 0) && setIsOpen(!isOpen)}
          disabled={collapsed ? workspaces.length === 0 : availableWorkspaces.length === 0}
          className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors overflow-hidden group"
          title={currentWorkspace?.name || 'Workspace'}
        >
          <>
            <WorkspaceTypeIcon workspaceType={currentWorkspace?.workspaceType} className="absolute inset-0 w-full h-full p-1.5 opacity-20" />
            <span className="relative z-10 text-xs font-bold text-slate-700 dark:text-zinc-200">
              {workspaceInitial}
            </span>
          </>
          {dropdownWorkspaces.length > 0 && <span className="absolute bottom-0.5 right-0.5 flex h-3 w-auto items-center gap-0.5">
            <ChevronRight className="h-3 w-3 text-slate-400 dark:text-zinc-500" />
          </span>}
        </button>

        {isOpen && dropdownPos && createPortal(dropdownMenu, document.body)}
      </div>
    );
  }

  // Single workspace — just show name, use branding color if available
  if (workspaces.length === 1) {
    return (
      <div className="px-3 py-2 text-sm text-slate-700 dark:text-zinc-300">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
          Workspaces
        </p>
        <div className="flex items-center gap-2">
          <Building2 aria-hidden="true" className="w-4 h-4 text-slate-400 dark:text-zinc-500 shrink-0" />
          <span className="min-w-0">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="block truncate font-medium text-xs">{currentWorkspace?.name || 'Workspace'}</span>
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-zinc-400"><WorkspaceTypeBadge workspaceType={currentWorkspace?.workspaceType} />{currentWorkspace?.role && <span>· {getWorkspaceRoleLabel(currentWorkspace)}</span>}</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
        Workspaces
      </p>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-xs text-slate-700 dark:text-zinc-300 transition-colors border ml-2 w-[calc(100%-0.5rem)] ${isOpen
          ? `rounded-t-none rounded-b-xl border-x border-b border-t-0 border-slate-200/80 ${currentWorkspace?.workspaceType === 'sandbox' ? 'bg-violet-100/90 dark:border-zinc-800/80 dark:bg-violet-950/50' : 'bg-emerald-100/90 dark:border-zinc-800/80 dark:bg-emerald-950/50'}`
          : 'rounded-lg border-transparent hover:bg-white dark:hover:bg-zinc-800'}`}
      >
        <Building2 aria-hidden="true" className="w-4 h-4 text-slate-400 dark:text-zinc-500 shrink-0" />
          <span className="flex-1 min-w-0 text-left">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="block truncate font-medium">{currentWorkspace?.name || 'Workspace'}</span>
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-normal text-slate-500 dark:text-zinc-400">
            <WorkspaceTypeBadge workspaceType={currentWorkspace?.workspaceType} />
            {currentWorkspace?.role && <span className="truncate">· {getWorkspaceRoleLabel(currentWorkspace)}</span>}
          </span>
        </span>
        {isOpen && <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />}
        <ChevronDown className={`w-3 h-3 text-slate-400 dark:text-zinc-500 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && dropdownPos && createPortal(dropdownMenu, document.body)}
    </div>
  );
}

export default WorkspaceSwitcher;
